import { contactPrimaryLabel } from "@/lib/contactDisplay";
import { propertyDisplayLabel } from "@/lib/propertyDisplay";
import type { ReachoutResolverContext, WhatsappTemplate } from "@/services/whatsappTemplateService";
import type { ContactSummary, PropertyDetail, PropertySummary } from "@/types/entities";
import { formatLongDateTime } from "@/utils/date-helpers";

function str(v: unknown): string {
  return typeof v === "string" && v.trim() ? v.trim() : "";
}

export function buildContactContext(
  contact: ContactSummary | null,
): Record<string, unknown> | null {
  if (!contact) return null;
  const fromParts = [contact.forename, contact.surname]
    .map((part) => str(part))
    .filter(Boolean)
    .join(" ")
    .trim();
  const name = str(contact.name) || fromParts || contactPrimaryLabel(contact);
  return { ...contact, name };
}

export function buildPropertyContext(
  summary: PropertySummary | null | undefined,
  detail: PropertyDetail | null | undefined,
): Record<string, unknown> | null {
  if (!summary && !detail) return null;

  const raw = { ...summary, ...detail } as Record<string, unknown>;
  const addressDetails = raw.addressDetails as Record<string, unknown> | undefined;

  const displayAddress =
    str(raw.displayAddress) ||
    propertyDisplayLabel({ ...summary, ...detail } as PropertySummary);

  const town =
    str(raw.town) ||
    str(addressDetails?.town) ||
    str(raw.locality) ||
    str(addressDetails?.locality) ||
    str(raw.county) ||
    str(addressDetails?.county) ||
    str(raw.postcode) ||
    str(addressDetails?.postcode);

  const postcode = str(raw.postcode) || str(addressDetails?.postcode);

  return {
    ...raw,
    displayAddress,
    town,
    postcode,
    bedrooms: raw.bedrooms ?? summary?.bedrooms ?? null,
    bathrooms: raw.bathrooms ?? summary?.bathrooms ?? null,
    propertyType: str(raw.propertyType) || str(summary?.propertyType) || null,
  };
}

export function formatReachoutAppointmentTime(
  value: string | null | undefined,
): string {
  return formatLongDateTime(value);
}

export function buildAppointmentContext(options: {
  startDate?: string | null;
  localDatetime?: string | null;
}): Record<string, unknown> | null {
  const time =
    (options.startDate
      ? formatReachoutAppointmentTime(options.startDate)
      : "") ||
    (options.localDatetime
      ? formatReachoutAppointmentTime(options.localDatetime)
      : "");

  if (!time) return null;

  return {
    time,
    startDate: options.startDate ?? null,
  };
}

export function templateUsesPathPrefix(
  template: WhatsappTemplate | null | undefined,
  prefix: string,
): boolean {
  if (!template?.paramMappings?.length) return false;
  return template.paramMappings.some((mapping) => {
    const paths = Array.isArray(mapping.path) ? mapping.path : [mapping.path];
    return paths.some((path) => path.startsWith(prefix));
  });
}

export interface BuildReachoutContextInput {
  contact: ContactSummary | null;
  propertySummary: PropertySummary | null;
  propertyDetail: PropertyDetail | null;
  legalEntityId: string | null;
  legalEntityName: string | null;
  branchId: string | null;
  branchName: string | null;
  appointmentStartDate?: string | null;
  appointmentLocal?: string | null;
}

export function buildReachoutResolverContext(
  input: BuildReachoutContextInput,
): ReachoutResolverContext {
  return {
    contact: buildContactContext(input.contact),
    legalEntity: input.legalEntityId
      ? {
          id: input.legalEntityId,
          name: input.legalEntityName,
          displayName: input.legalEntityName,
        }
      : null,
    branch: input.branchId ? { id: input.branchId, name: input.branchName } : null,
    property: buildPropertyContext(input.propertySummary, input.propertyDetail),
    appointment: buildAppointmentContext({
      startDate: input.appointmentStartDate,
      localDatetime: input.appointmentLocal,
    }),
  };
}
